import { memo, useEffect, useReducer } from "react";
import { motion } from "framer-motion";
import { Ear, Flag, GitBranch, MessageSquare, UserCheck } from "lucide-react";

import { cn } from "../../../lib/utils";
import { alertLabel } from "../alert-badge";
import { summaryOf } from "../lib/alerts";
import { SpeechCutChip } from "../drawer/speech-cut";
import { readCut } from "../lib/speech-cut";
import type { TranscriptLanguageMode } from "../lib/transcript-language";
import type { EngagementTier, RecentMessage, SupervisedCall } from "../types";
import { getManualFlag } from "../lib/flags-store";

const TIER_STYLE: Record<string, string> = {
  red: "border-l-[#ef4444] bg-[#fef2f2] dark:bg-[#450a0a]/35 border-red-200 dark:border-red-900/50",
  amber: "border-l-[#f59e0b] bg-[#fffbeb] dark:bg-[#451a03]/35 border-amber-200 dark:border-amber-900/50",
  green: "border-l-border bg-card border-border",
};

function Elapsed({ since }: { since?: string }) {
  const [, force] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const i = setInterval(force, 1000);
    return () => clearInterval(i);
  }, []);
  const s = Math.max(0, Math.floor((Date.now() - new Date(since || Date.now()).getTime()) / 1000));
  return (
    <span className="font-mono tnum text-[11px] text-foreground">
      {String(Math.floor(s / 60)).padStart(2, "0")}:{String(s % 60).padStart(2, "0")}
    </span>
  );
}

function Spark({ data }: { data?: number[] }) {
  const w = 56, h = 16;
  if (!data || data.length < 2) return <div style={{ width: w, height: h }} />;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v + 1) / 2) * h}`).join(" ");
  return (
    <svg width={w} height={h} className="opacity-70">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1" className="text-muted-foreground" />
    </svg>
  );
}

export interface CallTileProps {
  call: SupervisedCall;
  selected?: boolean;
  connected?: boolean;
  tier?: EngagementTier;
  currentUserId?: string | null;
  onSelect: (callId: string) => void;
  languageMode?: TranscriptLanguageMode;
  demoSeed?: number;
}

function CallTileImpl({
  call,
  selected = false,
  connected = false,
  tier: engagementTier = "watch",
  onSelect,
}: CallTileProps) {
  const summary = summaryOf(call);
  const activeSeverity =
    call.severity_open ??
    call.alerts?.top?.severity ??
    call.severity ??
    null;

  const isRed =
    call.risk?.level === "red" ||
    activeSeverity === "critical" ||
    (call as any).risk_tier === "red";

  const isAmber =
    !isRed &&
    (call.risk?.level === "amber" ||
      activeSeverity === "warn" ||
      (call as any).risk_tier === "amber" ||
      (summary.open ?? 0) > 0 ||
      Boolean(summary.top));

  const riskTier: "red" | "amber" | "green" = isRed ? "red" : isAmber ? "amber" : "green";
  const riskScore = call.risk?.score;

  // Parse turns preview from either recent or demo turns
  const anyCall = call as any;
  const turns: {
    seq: number | string;
    speaker: "ai" | "customer" | "supervisor";
    text: string;
    flags?: string[];
    cutRatio?: number;
  }[] = anyCall.turns?.length
    ? anyCall.turns.slice(-3)
    : (call.recent || []).slice(-3).map((r: RecentMessage, i: number) => {
        // A cut turn's `text` is what the model generated, which is routinely
        // longer than what was spoken. Show the voiced part and flag it, so the
        // tile stops implying the caller heard a sentence that never came out.
        const cut = readCut(r);
        return {
          seq: r.item_id || i,
          speaker: r.held ? "supervisor" : r.role === "assistant" ? "ai" : "customer",
          text: cut.visible ? cut.spoken : r.text,
          flags: [...(r.held ? ["steer"] : []), ...(cut.visible ? ["cut"] : [])],
          cutRatio: cut.visible ? cut.ratio : undefined,
        };
      });

  const topRule =
    summary.top?.detail ||
    (summary.top ? alertLabel(summary.top) : null) ||
    (anyCall.risk_reasons?.length ? anyCall.risk_reasons[anyCall.risk_reasons.length - 1] : null);

  // The session tier is global; it describes this tile only when this is the connected call.
  const isHeld = Boolean(call.holder || (connected && engagementTier === "takeover") || anyCall.state === "human_controlled");
  const isListening = Boolean((connected && engagementTier === "listen") || anyCall.state === "supervisor_listening");
  const isSteering = anyCall.state === "supervisor_steering";

  const stateBadge = isHeld ? "HUMAN" : isSteering ? "STEERING" : isListening ? "LISTENING" : anyCall.state ? anyCall.state.toUpperCase() : null;
  const isFlagged = Boolean(call.flag || getManualFlag(call.call_id));

  const sentimentTrend: number[] | undefined = anyCall.sentiment_trend;

  const customerLabel = call.contact_name || call.customer_name || anyCall.customer_ref || "Caller";
  const campaignLabel = call.campaign || call.agent_name || anyCall.campaign_name || "General";
  const langLabel: string | undefined = anyCall.language;

  return (
    <motion.div
      layout
      layoutId={call.call_id}
      transition={{ type: "spring", stiffness: 260, damping: 30 }}
      data-testid={`call-tile-${call.call_id}`}
      data-tier={riskTier}
      onClick={() => onSelect(call.call_id)}
      className={cn(
        `relative border border-border border-l-4 ${TIER_STYLE[riskTier] || TIER_STYLE.green} p-2 h-full min-h-[140px] flex flex-col justify-between cursor-pointer select-none hover:brightness-[0.98] transition overflow-hidden rounded-sm`,
        selected && "ring-2 ring-primary ring-offset-1"
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Elapsed since={call.created_at || anyCall.started_at} />
        {langLabel && (
          <span className="text-[10px] font-mono px-1 border border-border text-muted-foreground uppercase">
            {langLabel}
          </span>
        )}
        <span className="text-[10px] text-muted-foreground truncate">{campaignLabel}</span>
        <span className="ml-auto flex items-center gap-1">
          {isFlagged && <Flag size={11} className="text-amber-500 fill-current" title="Flagged for review" />}
          {isHeld && <UserCheck size={11} className="text-primary" />}
          {isListening && <Ear size={11} className="text-primary" />}
          {isSteering && <GitBranch size={11} className="text-primary" />}
          {stateBadge && (
            <span
              className={`text-[9px] font-mono px-1 rounded ${
                isHeld ? "bg-primary text-primary-foreground font-semibold" : "bg-muted text-muted-foreground"
              }`}
            >
              {stateBadge}
            </span>
          )}
          {call.is_demo && !stateBadge && (
            <span className="text-[8px] font-mono font-bold px-1 rounded bg-blue-500/20 text-blue-400">
              DEMO
            </span>
          )}
        </span>
      </div>

      <div className="flex items-center gap-2 mt-0.5">
        <span className="text-[10px] font-mono text-muted-foreground truncate font-medium">
          {customerLabel}
        </span>
        {riskScore != null && (
          <span
            className={`ml-auto text-[10px] font-mono tnum font-semibold ${
              riskTier === "red"
                ? "text-risk-red"
                : riskTier === "amber"
                ? "text-risk-amber"
                : "text-muted-foreground"
            }`}
          >
            {riskScore}
          </span>
        )}
      </div>

      <div className="mt-1 flex-1 min-h-0 flex flex-col justify-end gap-px overflow-hidden">
        {turns.length === 0 && <div className="text-[10px] text-muted-foreground italic">waiting for audio…</div>}
        {turns.map((t, i) => (
          <div
            key={t.seq}
            className={`text-[10px] leading-[13px] truncate ${
              i === turns.length - 1 ? "text-foreground font-medium" : "text-muted-foreground"
            }`}
          >
            <span
              className={`font-mono mr-1 font-semibold ${
                t.speaker === "ai"
                  ? "text-muted-foreground"
                  : t.speaker === "customer"
                  ? "text-foreground"
                  : "text-primary"
              }`}
            >
              {t.speaker === "ai" ? "AI" : t.speaker === "customer" ? "CU" : "SV"}
            </span>
            {t.cutRatio !== undefined ? (
              // Only the voiced words, plus the share that reached the caller.
              // Rendered only when a cut was measured, so nothing is inferred.
              <span
                className="ml-1 inline-flex shrink-0 items-center rounded border border-amber-300 bg-amber-100 px-1 font-mono text-[8px] font-bold text-amber-700"
                title="The caller cut the agent off here"
              >
                {t.cutRatio <= 0.001 ? "0%" : `${Math.round(t.cutRatio * 100)}%`}
              </span>
            ) : null}
            {t.flags?.includes("steer") ? `STEER -> ${t.text}` : t.text}
          </div>
        ))}
      </div>

      <div className="flex items-end justify-between mt-1 pt-1 border-t border-border/40">
        <Spark data={sentimentTrend} />
        {topRule ? (
          <span
            className={`text-[9px] font-mono truncate max-w-[130px] font-medium ${
              riskTier === "red" ? "text-risk-red" : "text-risk-amber"
            }`}
            data-testid={`tile-rule-${call.call_id}`}
          >
            {topRule}
          </span>
        ) : (
          <span className="text-[9px] font-mono text-muted-foreground flex items-center gap-1">
            <MessageSquare size={10} /> {call.message_count ?? anyCall.turns?.length ?? 0}
          </span>
        )}
      </div>
    </motion.div>
  );
}

export const CallTile = memo(CallTileImpl);
